<script lang="ts">
import { defineComponent } from 'vue'

// eslint-disable-next-line no-restricted-syntax
export enum CardVariant {
  DEFAULT = 'default',
  DISABLED = 'disabled',
}

export default defineComponent({
  name: 'SancareCard',
})
</script>

<script lang="ts" setup>
import { type Ref, ref, watch } from 'vue'

import SancareOcticon from './SancareOcticon.vue'
import { ComponentSize } from './types'

export interface SancareCardProps {
  headerText?: string | null
  isCollapsible?: boolean
  isContentScrollable?: boolean
  isExpanded?: boolean
  size?: ComponentSize
  title: string
  variant?: CardVariant
}

const props = withDefaults(defineProps<SancareCardProps>(), {
  headerText: null,
  isCollapsible: true,
  isContentScrollable: true,
  isExpanded: false,
  size: ComponentSize.MEDIUM,
  variant: CardVariant.DEFAULT,
})

const emit = defineEmits<{
  (e: 'click', cardTitle: string): void
}>()

const isExpanded: Ref<boolean> = ref(props.isCollapsible ? props.isExpanded : true)

watch(() => props.isExpanded, (newValue) => {
  isExpanded.value = newValue
})

function handleClick(): void {
  emit('click', props.title)
  isExpanded.value = !isExpanded.value
}
</script>

<template>
  <div
    class="sancare-card card"
    :class="[
      {
        'sancare-card--small': size === ComponentSize.SMALL,
        'sancare-card--scrollable': isContentScrollable,
      }
    ]"
  >
    <div
      class="sancare-card__header card-header"
      :class="[
        {
          'sancare-card__header--expanded': isExpanded,
          'sancare-card__header--collapsible': isCollapsible,
          'sancare-card__header--disabled': variant === CardVariant.DISABLED,
        }
      ]"
      @click="() => isCollapsible && handleClick()"
    >
      {{ title }}
      <div class="sancare-card__header__content-right">
        <div v-if="headerText">
          {{ headerText }}
        </div>
        <sancare-octicon
          v-if="isCollapsible"
          name="chevron-up"
          :width="12"
          :class="`sancare-card__header__content-right__icon ${
            isExpanded ? 'sancare-card__header__content-right__icon--rotated' : ''
          }`"
        />
      </div>
    </div>
    <div
      v-if="isExpanded"
      :id="`${title}`"
      class="sancare-card__content"
    >
      <div class="sancare-card__content__body card-body">
        <slot name="body" />
      </div>
      <div class="sancare-card__content__footer card-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>
